import StarrySkyBackground from "@/components/StarrySkyBackground";
import StoryCard from "@/components/StoryCard";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/services/auth-service";
import { motion } from "framer-motion";
import { Book, Search } from "lucide-react";
import { useEffect, useState } from 'react';

interface Story {
    id: number;
    title: string;
    coverImageUrl?: string;
    createdAt: string;
}


const LibraryPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteStoryId, setDeleteStoryId] = useState<number | null>(null);
    const [stories, setStories] = useState<Story[]>([]);

    useEffect(() => {
        loadStories();
    }, []);


    const loadStories = async () => {
        try {
            const response = await api.get('/api/generated-stories/');
            const data = response.data
            setStories([...data]);
        } catch (error) {
            console.error('Error loading stories:', error);
        }
    };


    const filteredStories = stories.filter(story =>
        story.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleDeleteStory = async () => {
        if (deleteStoryId) {
            await api.delete(`/api/stories/${deleteStoryId}/`);
            setStories(prev => prev.filter(story => story.id !== deleteStoryId));
            setDeleteStoryId(null);
        }
    };

    const handleTitleUpdate = async (storyId: number, newTitle: string) => {
        const body = { "title": newTitle};
        await api.patch(`/api/stories/${storyId}/update_title/`, body);
        setStories(prev => prev.map(story =>
            story.id === storyId ? { ...story, title: newTitle } : story
        ));
    };

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    };

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col">
            <StarrySkyBackground showAurora={true} showClouds={true} showShootingStar={false} />

            {/* Fixed header section */}
            <div className="p-4 pb-4 bg-background/10 backdrop-blur-sm">
                <div className="container mx-auto">
                    <div className="flex flex-col items-center justify-between gap-4 mb-2 md:flex-row">
                        <h1 className="text-3xl font-bold">My Library</h1>
                        <div className="flex items-center w-full gap-4 md:w-auto">
                            <div className="relative flex-1 md:w-64">
                                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Search stories..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                            <Badge variant="secondary" className="text-sm whitespace-nowrap">
                                {filteredStories.length} {filteredStories.length === 1 ? 'Story' : 'Stories'}
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>


            <div className="flex-1 p-4 pt-0 overflow-y-auto lg:p-8">
                <div className="container mx-auto">
                    <motion.div
                        variants={container}
                        initial="hidden"
                        animate="show"
                        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    >
                        {filteredStories.map((story) => (
                            <motion.div key={story.id} variants={item}>
                                <StoryCard
                                    story={story}
                                    onDelete={(id) => setDeleteStoryId(id)}
                                    onTitleUpdate={handleTitleUpdate}
                                />
                            </motion.div>
                        ))}
                    </motion.div>

                    {filteredStories.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col items-center justify-center min-h-[50vh] mt-8"
                        >
                            <Book className="w-16 h-16 mx-auto mb-4" />
                            <h2 className="mb-2 text-xl font-semibold">
                                {searchQuery ? 'No stories found' : 'Your library is empty'}
                            </h2>
                            <p>
                                {searchQuery
                                    ? 'Try adjusting your search terms'
                                    : 'Start creating magical stories for your little ones'}
                            </p>
                        </motion.div>
                    )}
                </div>
            </div>

            <AlertDialog open={!!deleteStoryId} onOpenChange={() => setDeleteStoryId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Story</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this story? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <Button variant="destructive" onClick={handleDeleteStory}>
                            Delete
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default LibraryPage;
